import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { publicApi, apiError } from "@/lib/api";
import { Star, Check, ArrowRight, CheckCircle2, Fuel } from "lucide-react";
import { modal } from "@/lib/modal";

export default function Pesquisa() {
  const { aid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [nota, setNota] = useState(0);
  const [hover, setHover] = useState(0);
  const [ofereceu, setOfereceu] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicApi.get(`/pesquisa/atendimento/${aid}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(apiError(e)));
  }, [aid]);

  const submit = async () => {
    if (nota < 1 || nota > 5) return modal.error("Selecione uma nota de 1 a 5");
    if (ofereceu === null) return modal.error("Responda sobre os serviços");
    setSending(true);
    try {
      await publicApi.post("/pesquisa/responder", {
        atendimento_id: aid,
        frentista_id: data?.atendimento?.frentista_id,
        nota,
        ofereceu_servicos: ofereceu,
      });
      setDone(true);
    } catch (e) { modal.error(apiError(e)); }
    finally { setSending(false); }
  };

  if (error) {
    return (
      <Wrap>
        <div className="text-center mt-8">
          <div className="font-serif-display text-2xl mb-2">Pesquisa indisponível</div>
          <p className="text-sm text-neutral-600">{error}</p>
        </div>
      </Wrap>
    );
  }
  if (!data) return <Wrap><div className="text-center mt-8 text-neutral-500">Carregando...</div></Wrap>;
  if (done) {
    return (
      <Wrap>
        <div className="text-center mt-8 float-in" data-testid="pesquisa-thanks">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg">
            <CheckCircle2 size={28} className="text-white" />
          </div>
          <h1 className="font-serif-display text-3xl mt-5">Obrigado!</h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-xs mx-auto">
            Seu retorno chegou. Continuamos melhorando para você ter um atendimento ainda melhor.
          </p>
        </div>
      </Wrap>
    );
  }

  const fname = data.frentista_nome || "—";

  return (
    <Wrap>
      <div className="float-in" data-testid="pesquisa-form">
        <div className="chip mb-2 text-[10px] py-0.5">Pesquisa de Satisfação</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Como foi seu atendimento?</h1>
        <p className="text-sm text-neutral-600 mt-1">
          Atendido(a) por <b>{fname}</b>{data.unit_nome ? ` no ${data.unit_nome}` : ""}.
        </p>

        <div className="card-soft rounded-2xl p-4 mt-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Sua nota</div>
          <div className="flex items-center gap-2 justify-center" data-testid="stars">
            {[1, 2, 3, 4, 5].map((n) => {
              const filled = (hover || nota) >= n;
              return (
                <button key={n} type="button"
                        data-testid={`star-${n}`}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => setNota(n)}
                        className="transition-transform active:scale-95">
                  <Star size={42} className={filled ? "text-amber-400 fill-amber-400" : "text-neutral-300"} />
                </button>
              );
            })}
          </div>
        </div>

        <div className="card-soft rounded-2xl p-4 mt-3">
          <div className="text-sm font-medium leading-snug">
            O atendente ofereceu todos os serviços?
          </div>
          <div className="text-[11px] text-neutral-500 mt-0.5">
            Lavar vidros, verificar água e óleo, calibrar pneus.
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button data-testid="serv-sim" onClick={() => setOfereceu(true)}
                    className={`h-12 rounded-full text-[13px] font-medium transition-all border ${ofereceu === true ? "bg-emerald-600 text-white border-emerald-600 shadow-md" : "bg-white border-neutral-200 hover:border-neutral-300"}`}>
              <Check size={14} className="inline mr-1.5" /> Sim
            </button>
            <button data-testid="serv-nao" onClick={() => setOfereceu(false)}
                    className={`h-12 rounded-full text-[13px] font-medium transition-all border ${ofereceu === false ? "bg-rose-600 text-white border-rose-600 shadow-md" : "bg-white border-neutral-200 hover:border-neutral-300"}`}>
              Não
            </button>
          </div>
        </div>

        <button data-testid="pesquisa-submit" onClick={submit} disabled={sending || !nota || ofereceu === null}
                className="w-full mt-4 h-12 btn-primary-blue text-[13px] font-medium flex items-center justify-center gap-1.5">
          {sending ? "Enviando..." : (<>Enviar avaliação <ArrowRight size={14} /></>)}
        </button>
      </div>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen flex flex-col" data-testid="pesquisa-page">
      <header className="max-w-md mx-auto w-full px-5 py-4 flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl glass flex items-center justify-center">
          <Fuel size={15} className="text-[color:var(--brand-blue)]" />
        </div>
        <div className="font-serif-display text-base">Postos Fidelidade</div>
      </header>
      <main className="flex-1 max-w-md mx-auto w-full px-5 pb-10">{children}</main>
    </div>
  );
}
